import { ChangeEvent, useRef, useState, useEffect, useMemo } from 'react'
import DateTimeRangeFilter, {
  DateFilterObject,
} from '../../../../common/filter/DateTimeRangeFilter'
import { SelectData } from '../../../../common/filter/models'
import { selectType as cashBoxSelectType } from '../../../../common/filter/CashBoxFilter'
import MerchantFilter, {
  selectType as merchantSelectType,
} from '../../../../common/filter/MerchantFilter'
import PaymentTicketStatusesFilter, {
  selectType as statusesSelectType,
} from '../../../../common/filter/PaymentTicketStatusesFilter'
import { selectType as processTypeSelectType } from '../../../../common/filter/PaymentTicketProcessTypeFilter'
import { selectType as ticketTypeFilterType } from '../../../../common/filter/TicketTypeFilter'
import { MatchingPaymentFinishedTicketsFilters } from '../../core/_models'

const FinishedPaymentTicketsFilter = ({
  defaultFilters,
  applyFilters,
  visibility,
}: {
  defaultFilters: MatchingPaymentFinishedTicketsFilters
  applyFilters: (newFilters: MatchingPaymentFinishedTicketsFilters) => void
  visibility: Record<string, boolean>
}) => {
  const defaultFormState = useMemo(
    () => ({
      ...defaultFilters,
      byCompletionDate: defaultFilters.byCompletionDate && visibility.completionDate ? true : false,
      search: visibility.ticketToken ? defaultFilters.search || '' : undefined,
    }),
    [defaultFilters, visibility]
  )

  const [formState, setFormState] =
    useState<MatchingPaymentFinishedTicketsFilters>(defaultFormState)

  const [filterBy, setFilterBy] = useState<'byCreatedDate' | 'byCompletionDate'>(
    defaultFilters.byCompletionDate ? 'byCompletionDate' : 'byCreatedDate'
  )

  const searchMerchantRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setFormState(defaultFormState)
    setFilterBy(defaultFormState.byCompletionDate ? 'byCompletionDate' : 'byCreatedDate')
  }, [defaultFormState])

  const handleDateTimeChange = (filterData: DateFilterObject) => {
    setFormState((prev: any) => ({
      ...prev,
      dateStart: filterData.dateStart,
      dateEnd: filterData.dateEnd,
    }))
  }

  const handleFilterByChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === 'byCompletionDate' ? 'byCompletionDate' : 'byCreatedDate'
    setFilterBy(value)
    setFormState((prev: any) => ({
      ...prev,
      byCompletionDate: value === 'byCompletionDate',
    }))
  }

  /** @todo Отрефакторить это */
  const handleSelectChange = (selectData: SelectData) => {
    setFormState((prev: any) => {
      const next = { ...prev }
      switch (selectData.type) {
        case cashBoxSelectType:
          if (!selectData.value.length) {
            delete next.cashBoxId
          } else {
            next.cashBoxId = selectData.value
          }
          break
        case merchantSelectType:
          if (!selectData.value.length) {
            delete next.merchantTokenList
          } else {
            next.merchantTokenList = selectData.value
          }
          break
        case statusesSelectType:
          next.statusList = selectData.value
          break
        case processTypeSelectType:
          if (!selectData.value.length) {
            delete next.ticketProcessType
          } else {
            next.ticketProcessType = selectData.value
          }
          break
        case ticketTypeFilterType:
          if (!selectData.value.length) {
            delete next.source
          } else {
            next.source = selectData.value
          }
          break
      }
      return next
    })
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setFormState((prev: any) => ({ ...prev, search: value }))
  }

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    applyFilters(formState)
  }

  const handleReset = () => {
    setFormState(defaultFormState)
    setFilterBy('byCreatedDate')
    if (searchMerchantRef.current) {
      searchMerchantRef.current.value = ''
    }
    applyFilters(defaultFormState)
  }

  const isFilterFormVisible = useMemo(() => {
    return (
      visibility.ticketToken ||
      visibility.completionDate ||
      visibility.merchant ||
      visibility.status
    )
  }, [visibility])

  if (!isFilterFormVisible) return null

  return (
    <div className='card mb-7'>
      <div className='card-body border-0 pt-5'>
        <form className='row' onSubmit={handleSubmit}>
          {visibility?.completionDate && (
            <div className='col-lg-8'>
              <div className='row mb-7'>
                <div className='col'>
                  <div className='form-check form-check-custom form-check-solid'>
                    <input
                      id='by-created-date-radio'
                      className='form-check-input'
                      type='radio'
                      value='byCreatedDate'
                      name='report-sort-type-radio'
                      onChange={handleFilterByChange}
                      checked={filterBy === 'byCreatedDate'}
                    />
                    <label className='form-check-label' htmlFor='by-created-date-radio'>
                      По дате создания
                    </label>
                  </div>
                </div>
                <div className='col'>
                  <div className='form-check form-check-custom form-check-solid'>
                    <input
                      id='by-completion-date-radio'
                      className='form-check-input'
                      type='radio'
                      value='byCompletionDate'
                      name='report-sort-type-radio'
                      onChange={handleFilterByChange}
                      checked={filterBy === 'byCompletionDate'}
                    />
                    <label className='form-check-label' htmlFor='by-completion-date-radio'>
                      По дате закрытия
                    </label>
                  </div>
                </div>
              </div>
              <DateTimeRangeFilter filter={formState} updateTime={handleDateTimeChange} />
            </div>
          )}
          <div className='d-flex flex-column col-lg-4 gap-4 mt-4 mt-lg-0 '>
            <button type='submit' className='btn btn-success '>
              Фильтровать
            </button>
            <button type='button' className='btn btn-primary' onClick={handleReset}>
              Сбросить фильтр
            </button>
          </div>
          {visibility?.ticketToken && (
            <div className='col-lg-4 mt-8'>
              <div className='position-relative  me-md-2'>
                <div className='svg-icon svg-icon-3 svg-icon-gray-500 position-absolute top-50 start-0 translate-middle ms-5'>
                  <i className='fa fa-search'></i>
                </div>
                <input
                  ref={searchMerchantRef}
                  type='text'
                  name='search'
                  className='form-control form-control-solid ps-10 pt-3 pb-2'
                  onChange={handleSearchChange}
                  value={formState.search || ''}
                  placeholder='ID тикета или ID транзакции'
                />
              </div>
            </div>
          )}
          {visibility?.merchant && (
            <div className='col-lg-4 mt-8'>
              <MerchantFilter
                changeSelect={handleSelectChange}
                value={
                  Array.isArray(formState.merchantTokenList) ? formState.merchantTokenList : []
                }
                isMultiple
              />
            </div>
          )}
          {visibility?.status && (
            <div className='col-lg-4 mt-8'>
              <PaymentTicketStatusesFilter
                changeSelect={handleSelectChange}
                isMultiple={true}
                value={(formState.statusList as string[]) || []}
              />
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default FinishedPaymentTicketsFilter
